import React, { useMemo, useState } from 'react';
import WorkspacePage from '../components/layout/WorkspacePage';
import Dashboard from '../components/Dashboard';
import ActionBriefing from '../components/dashboard/ActionBriefing';
import SummaryStrip from '../components/dashboard/SummaryStrip';
import DirectorApprovalQueuePanel from '../components/director/DirectorApprovalQueuePanel';
import ReviewActionBar from '../components/requests/ReviewActionBar';
import OperationalRequestEntrySection from '../components/workspace/OperationalRequestEntrySection';
import OperationalPerformanceViews from '../components/workspace/OperationalPerformanceViews';
import OperationalSummaryGrid from '../components/workspace/OperationalSummaryGrid';
import OperationalSharedCalendar from '../components/workspace/OperationalSharedCalendar';
import OperationalRequestWorkspace from '../components/workspace/OperationalRequestWorkspace';
import ReturnedItemsBanner from '../components/workspace/ReturnedItemsBanner';
import Button from '../components/ui/Button';
import { getAirportWorkspaceMeta } from '../components/workspace/operationalWorkspaceMeta';
import { canReviewRequest } from '../components/requests/requestUtils';
import useCurrentManagerRecord from '../hooks/useCurrentManagerRecord';
import useLocationPerformanceContext from '../hooks/useLocationPerformanceContext';
import useOperationalRequestQueue from '../hooks/useOperationalRequestQueue';
import { apiRequest } from '../services/apiClient';
import { downloadCsv } from '../utils/csvExport';
import useReturnedApprovalItems from '../hooks/useReturnedApprovalItems';
import { useTenantModules } from '../context/TenantConfigContext';
import { formatRoleLabel } from '../config/appRoutes';
import './RoleDashboards.css';

function countFuturePolicyDays(policies = [], policyTypes = []) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = new Set();

  policies.forEach((policy) => {
    if (!policy?.isActive || !policyTypes.includes(policy.policyType)) {
      return;
    }

    const start = new Date(`${policy.startDate}T00:00:00`);
    const end = new Date(`${(policy.endDate || policy.startDate)}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return;
    }

    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      if (date >= today) {
        days.add(date.toISOString().slice(0, 10));
      }
    }
  });

  return days.size;
}

function AirportManagerDashboard({
  authorityLabel = '',
  currentPath,
  dashboardData,
  onNavigate = () => {},
  period,
  selectedManager,
  scopeLabel = '',
  stationPolicies = [],
  stationRequests,
  stationSummary,
  stationManagers,
  onManagerChange,
  userDisplayName = '',
  userReferenceTitle = '',
  user,
  workspaceLabel = 'Operational Workspace'
}) {
  const { isModuleEnabled } = useTenantModules();
  const {
    heroDescription,
    heroTitle,
    isOpsManagersRoute,
    isRequestsCalendarRoute,
    isStationDashboardRoute
  } = getAirportWorkspaceMeta(currentPath);
  const [reviewNote, setReviewNote] = useState('');
  const [requestActionError, setRequestActionError] = useState('');
  const [requestActionLoading, setRequestActionLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState('');
  const [requestHistoryVersion, setRequestHistoryVersion] = useState(0);
  const { performanceContext, updatePerformanceContext } = useLocationPerformanceContext(stationSummary.locationCode);
  const canSubmitRequests = Boolean(user?.permissions?.includes('submit_vacation_request'));
  const requestsEnabled = isModuleEnabled('requests');
  const returnedItems = useReturnedApprovalItems();
  const handleSelectionCleared = React.useCallback(() => {
    setReviewNote('');
    setRequestActionError('');
  }, []);
  const {
    filteredRequests,
    localRequests,
    requestFilter,
    requestSummary,
    selectedRequest,
    selectedRequestId,
    setLocalRequests,
    setRequestFilter,
    setSelectedRequestId
  } = useOperationalRequestQueue({
    initialFilter: 'pending',
    onSelectionCleared: handleSelectionCleared,
    requests: stationRequests
  });

  const selfRecord = useMemo(
    () => stationManagers.find(manager => Number(manager.managerDbId) === Number(user?.managerId)) || null,
    [stationManagers, user?.managerId]
  );
  const currentManagerRecord = useCurrentManagerRecord({
    fallbackRoleTitle: formatRoleLabel('airport_manager'),
    sourceRecord: selfRecord,
    user
  });

  const selectedRequestCanReview = canReviewRequest(selectedRequest);
  const upcomingTimeOff = useMemo(
    () => localRequests.filter(request => new Date(`${request.startDate}T00:00:00`) >= new Date()).length,
    [localRequests]
  );
  const coverageRiskDays = useMemo(
    () => countFuturePolicyDays(stationPolicies, ['max_managers_off']),
    [stationPolicies]
  );
  const summaryItems = useMemo(() => ([
    {
      label: 'Location At Work',
      value: `${Math.round(stationSummary.atWorkRate || 0)}%`,
      subtitle: `${stationSummary.managerCount} managers in the current location view`,
      tone: 'good',
      icon: 'fa-user-check'
    },
    ...(requestsEnabled ? [
      {
        label: 'Review Needed',
        value: requestSummary.reviewNeeded,
        subtitle: 'Requests flagged for closer review',
        tone: 'warning',
        icon: 'fa-magnifying-glass'
      },
      {
        label: 'Pending Requests',
        value: requestSummary.pending,
        subtitle: 'Requests waiting for station action',
        tone: 'info',
        icon: 'fa-list-check'
      }
    ] : []),
    {
      label: 'Managers',
      value: stationSummary.opsManagerCount,
      subtitle: 'Managers reporting inside your location',
      tone: 'neutral',
      icon: 'fa-users'
    },
    ...(requestsEnabled ? [
      {
        label: 'Coverage Risk Days',
        value: coverageRiskDays,
        subtitle: 'Future days with coverage rules in place',
        tone: 'danger',
        icon: 'fa-users-line'
      },
      {
        label: 'Upcoming Time Off',
        value: upcomingTimeOff,
        subtitle: 'Visible future station requests',
        tone: 'neutral',
        icon: 'fa-calendar-days'
      }
    ] : [])
  ]), [coverageRiskDays, requestSummary.pending, requestSummary.reviewNeeded, requestsEnabled, stationSummary, upcomingTimeOff]);
  const locationSnapshotItems = useMemo(() => ([
    {
      key: 'location',
      label: 'Location',
      value: stationSummary.locationName,
      description: stationSummary.locationCode
    },
    {
      key: 'people',
      label: 'People In Scope',
      value: stationSummary.opsManagerCount,
      description: 'Visible operational users inside this location'
    },
    {
      key: 'pending-requests',
      label: 'Pending Time-Off Requests',
      value: stationSummary.pendingRequests,
      description: 'Requests requiring location review'
    }
  ]), [stationSummary.locationCode, stationSummary.locationName, stationSummary.opsManagerCount, stationSummary.pendingRequests]);
  const exportRequests = () => {
    downloadCsv(
      `station-requests-${String(stationSummary.locationCode || 'station').toLowerCase()}-${requestFilter.replace(/\s+/g, '-')}.csv`,
      [
        { label: 'Requester', value: 'managerName' },
        { label: 'Location', value: row => row.locationCode || stationSummary.locationCode },
        { label: 'Start Date', value: 'startDate' },
        { label: 'End Date', value: 'endDate' },
        { label: 'Time Block', value: 'timeBlock' },
        { label: 'Status', value: 'status' },
        { label: 'Reason', value: 'reason' }
      ],
      filteredRequests
    );
  };

  const handleRequestDecision = async (decision) => {
    if (!selectedRequest?.id || requestActionLoading) {
      return;
    }

    setPendingAction(decision);
    setRequestActionLoading(true);
    setRequestActionError('');

    try {
      const path = decision === 'approve' ? '/api/events/vacation-approve' : '/api/events/vacation-deny';
      const payload = decision === 'approve'
        ? { id: selectedRequest.id }
        : { id: selectedRequest.id, reason: reviewNote || null };

      const updatedRequest = await apiRequest(path, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      setLocalRequests(current => current.map(request => (
        String(request.id) === String(updatedRequest.id) ? { ...request, ...updatedRequest } : request
      )));
      setRequestHistoryVersion(current => current + 1);
      setRequestActionError('');
      if (decision === 'deny') {
        setReviewNote('');
      }
    } catch (error) {
      setRequestActionError(error.message || `Unable to ${decision} request.`);
    } finally {
      setRequestActionLoading(false);
      setPendingAction('');
    }
  };

  // Sprint 3B.5: AirportManagerDashboard migrated to WorkspacePage with
  // width="operational". The OperationalWorkspaceHero contents are
  // re-mapped onto WorkspacePage's eyebrow / title / subtitle /
  // contextSlot — the standalone hero component is no longer rendered
  // by this page (OpsManagerDashboard does the same mapping in 3B.5).
  // The .role-page wrapper is retained as an INNER element so every
  // existing RoleDashboards.css selector keeps applying without a
  // stylesheet rewrite.
  const headerEyebrow = (
    <span className="role-badge">{workspaceLabel}</span>
  );
  const headerContextChips = (
    <div className="role-context-row">
      <div className="role-scope-chip" title="The signed-in person using this workspace.">
        <span>Profile</span>
        <strong>{userDisplayName || user?.email || 'Authenticated user'}</strong>
      </div>
      {userReferenceTitle ? (
        <div className="role-scope-chip" title="Job title stored for reference.">
          <span>Title</span>
          <strong>{userReferenceTitle}</strong>
        </div>
      ) : null}
      <div className="role-scope-chip" title="The active scope that filters visible records, scorecards, and requests.">
        <span>Scope</span>
        <strong>{scopeLabel || stationSummary.locationCode || 'Assigned location'}</strong>
      </div>
      {authorityLabel ? (
        <div className="role-scope-chip" title="Additional permissions granted to this account beyond its default workspace permissions.">
          <span>Permissions</span>
          <strong>{authorityLabel}</strong>
        </div>
      ) : null}
    </div>
  );

  return (
    <WorkspacePage
      data-workspace-id="airport-manager-dashboard"
      width="operational"
      eyebrow={headerEyebrow}
      title={heroTitle}
      subtitle={heroDescription}
      contextSlot={headerContextChips}
    >
      <div className="role-page">
        <SummaryStrip items={summaryItems} />

      <ReturnedItemsBanner items={returnedItems} />

      <OperationalPerformanceViews
        currentPath={currentPath}
        dashboardData={dashboardData}
        locationCode={stationSummary.locationCode}
        locationName={stationSummary.locationName}
        onPerformanceContextChange={updatePerformanceContext}
        period={period}
        performanceContext={performanceContext}
        selectedManager={selectedManager}
      />

      {(isStationDashboardRoute || isOpsManagersRoute) && (
        <OperationalSummaryGrid items={locationSnapshotItems} />
      )}

      {isStationDashboardRoute && requestsEnabled && (
        <ActionBriefing
          heading="Open action items"
          items={[
            { count: returnedItems.length, label: `action item${returnedItems.length !== 1 ? 's' : ''} returned for revision`, tone: 'danger', path: '/requests-calendar', onNavigate },
            { count: requestSummary.pending, label: 'request action items awaiting decision', tone: 'warning', path: '/requests-calendar', onNavigate },
            { count: requestSummary.reviewNeeded, label: 'policy action items that need closer review', tone: 'info', path: '/requests-calendar', onNavigate },
            { count: coverageRiskDays, label: 'upcoming coverage action items', tone: 'neutral', path: '/requests-calendar', onNavigate }
          ]}
        />
      )}

      {isStationDashboardRoute && (
        <div className="role-dashboard-shell role-dashboard-shell-airport">
          <div className="role-layout-main">
            <div className="role-section">
              <div className="role-section-header">
                <h2>
                  Scorecard
                  {stationManagers.find(m => String(m.id) === String(selectedManager))?.name
                    ? ` — ${stationManagers.find(m => String(m.id) === String(selectedManager)).name}`
                    : ''}
                </h2>
                <span>Selectable person</span>
              </div>
              <div className="role-inline-control">
                <label htmlFor="airport-manager-view">Review person</label>
                <select
                  id="airport-manager-view"
                  className="role-select"
                  value={selectedManager}
                  onChange={(event) => onManagerChange(event.target.value)}
                >
                  {stationManagers.map(manager => (
                    <option key={manager.id} value={manager.id}>{manager.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <Dashboard
              data={dashboardData}
              period={period}
              manager={selectedManager}
              dataVersion={0}
            />
          </div>

          <div className="role-layout-side role-stack">
            <div className="role-section">
              <div className="role-section-header">
                <h2>People In This Location View</h2>
                <span>{stationManagers.length} visible</span>
              </div>
              <div className="table-card">
                <table className="role-table airport-station-managers-table">
                  <thead>
                    <tr>
                      <th className="airport-station-managers-col-name">Name</th>
                      <th className="airport-station-managers-col-role">Role</th>
                      <th className="airport-station-managers-col-email">Email</th>
                      <th className="airport-station-managers-col-station">Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stationManagers.map(manager => (
                      <tr key={manager.id}>
                        <td className="airport-station-managers-col-name">{manager.name}</td>
                        <td className="airport-station-managers-col-role">{manager.roleTitle}</td>
                        <td className="airport-station-managers-col-email">{manager.email}</td>
                        <td className="airport-station-managers-col-station">{manager.locationCode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {isRequestsCalendarRoute && (
        <div className="role-stack">
          <OperationalRequestEntrySection
            canSubmit={canSubmitRequests}
            currentManager={currentManagerRecord}
            disabledMessage="This account can review location requests, but it cannot submit its own PTO requests right now."
            onSubmitted={(event) => {
              setLocalRequests(current => [event, ...current.filter(item => String(item.id) !== String(event.id))]);
              setSelectedRequestId(String(event.id));
            }}
            scopeCode={stationSummary.locationCode}
            submitCopy="Submit your own PTO request here."
            title="Request Time Off"
          />

          <div className="role-section">
            <OperationalRequestWorkspace
              sectionTitle="Location Vacation Requests"
              countLabel="shown"
              introCopy="Review current request outcomes and recent history for your location."
              exportAction={(
                <Button size="sm" variant="secondary" onClick={exportRequests} disabled={filteredRequests.length === 0}>
                  Export CSV
                </Button>
              )}
              requestFilter={requestFilter}
              requestSummary={requestSummary}
              filteredRequests={filteredRequests}
              isInitialLoading={filteredRequests.length === 0 && localRequests === stationRequests && stationRequests.length === 0 && !requestSummary.approved && !requestSummary.denied}
              emptyMessage="No vacation requests found for this location."
              noResultsColSpan={6}
              selectedRequestId={selectedRequestId}
              showRequesterColumn
              skeletonColumnCount={6}
              tableClassName="airport-requests-table"
              detailRequest={selectedRequest}
              detailRequestTitle="Selected Location Request"
              detailEmptyMessage="Select a location request above to review the latest decision and audit history."
              detailFooter={selectedRequest ? (
                selectedRequestCanReview ? (
                  <ReviewActionBar
                    errorMessage={requestActionError}
                    isSubmitting={requestActionLoading}
                    pendingAction={pendingAction}
                    onApprove={() => handleRequestDecision('approve')}
                    onDeny={() => handleRequestDecision('deny')}
                    onReviewNoteChange={setReviewNote}
                    reviewNote={reviewNote}
                  />
                ) : (
                  <p className="role-section-copy role-inline-message">
                    This request already has a final outcome. History remains available on the right.
                  </p>
                )
              ) : null}
              detailHistoryRefreshKey={requestHistoryVersion}
              detailHistoryTitle="Location Request History"
              onSelectQueue={setRequestFilter}
              onSelectRequest={setSelectedRequestId}
            />
          </div>
        </div>
      )}

      {isRequestsCalendarRoute && requestsEnabled && (
        <>
          <DirectorApprovalQueuePanel
            selectedLocationCode={stationSummary.locationCode}
            locationLabel={`${stationSummary.locationCode} - ${stationSummary.locationName}`}
            user={user}
          />
          <OperationalSharedCalendar
            locationCode={stationSummary.locationCode}
            locationLabel={stationSummary.locationName}
            subtitle="Location calendar for approved time off, blackout dates, and visible coverage rules."
            title="Location Shared Calendar"
            workspaceLabel={workspaceLabel}
          />
        </>
      )}
      </div>
    </WorkspacePage>
  );
}

export default AirportManagerDashboard;
